<script lang="ts">
	import type { Catalog } from './types/catalog'
	import { modals } from 'svelte-modals'
	import MaterialCatalogModal from './MaterialCatalogModal.svelte'
	import { t } from '$i18n/i18n'
	import ActionMenu from '#lib/organisms/ActionMenu.svelte'
	import ActionMenuItem from '#lib/molecules/ActionMenuItem.svelte'
	import type { Filter } from '$utils/filter'
	import { applyFilters } from '$utils/filter'
	import { DefaultCatalog } from './types/catalog'
	import { mmpvState } from './mmpv.state.svelte'
	import iconEditGreen from '@/icons/icon_edit_green.svg'
	import iconTrashRed from '@/icons/icon_trash_red.svg'
	import { handleDeleteCatalog, fetchCatalogs } from '#lib/mmpv/mmpv-catalogs.controller.svelte.js'

	interface Props {
		onCatalogSelected?: () => void
	}

	let { onCatalogSelected }: Props = $props()

	const filters: Filter[] = $derived(mmpvState.filters)

	function selectCatalog(catalog: Catalog) {
		mmpvState.selectedCatalog = catalog
		onCatalogSelected?.() // to change the view in the parent
	}

	function editCatalog(c: Catalog) {
		modals.open(MaterialCatalogModal, {
			update: fetchCatalogs,
			catalog: { ...DefaultCatalog(), ...c }
		})
	}

	function handleRowKeydown(event: KeyboardEvent, select: () => void) {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			select()
		}
	}
</script>

<div class="catalogs-container">
	<div class="catalog-table">
		<div class="row-wrapper">
			<div></div>
			<div>{$t('UI.mmpv.catalogs.name')}</div>
			<!--<div>{$t('UI.mmpv.catalogs.category')}</div>-->
			<div>{$t('UI.mmpv.catalogs.comment')}</div>
			<div>{$t('UI.mmpv.catalogs.maxDepth')}</div>
			<div>{$t('UI.mmpv.catalogs.nodeCount')}</div>
			<div>{$t('UI.mmpv.catalogs.action')}</div>
			<div></div>
		</div>
		{#each mmpvState.catalogs.filter((c) => applyFilters(filters, c)) as catalog (catalog.id)}
			{const selectGivenCatalog = () => selectCatalog(catalog)}
			<div class="row-wrapper">
				<div></div>
				<div
					role="button"
					tabindex="0"
					onclick={selectGivenCatalog}
					onkeydown={(e) => handleRowKeydown(e, selectGivenCatalog)}
				>
					{catalog.name || ''}
				</div>
				<div>{catalog.comment || ''}</div>
				<div>{catalog.maxDepth || ''}</div>
				<div>{catalog.nodes.length || 0}</div>
				<div style="position: relative">
					<ActionMenu
						titleAttr={$t('UI.mmpv.actionMenu.titleAttribute')}
						appendToElement={document.body}
					>
						{#snippet content()}
							<ActionMenuItem
								label={$t('UI.edit')}
								iconSrc={iconEditGreen}
								onclick={() => editCatalog(catalog)}
							/>
							<ActionMenuItem
								label={$t('UI.delete')}
								iconSrc={iconTrashRed}
								onclick={() => handleDeleteCatalog(catalog)}
							/>
						{/snippet}
					</ActionMenu>
				</div>
				<div></div>
			</div>
		{/each}
	</div>
</div>

<style>
	@import '$styles/global.scss';

	.catalogs-container {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		align-items: center;

		width: 100%;
	}

	.catalog-table {
		display: grid;
		grid-template-columns:
			68px 1fr 2fr repeat(3, auto)
			68px; /* Adjust this when category is used again. */
		grid-template-rows: 1fr; /* remove for unequal rows */

		font-family: Roboto, sans-serif;
		line-height: 18px;
		font-weight: 400;
		font-size: 0.75rem;
		color: black;

		width: 100%;

		& .row-wrapper {
			display: contents;

			& > div {
				text-align: start;
				border-bottom: 1px solid var(--color-gray-100);
				padding: 0.75rem 0.5rem;

				&:nth-last-child(2) {
					display: flex;
					flex-direction: row;
					align-items: center;
					justify-content: center;
				}

				&:hover {
					cursor: pointer;
				}
			}

			&:first-child > div {
				border-bottom: 1px solid var(--primary-green);

				font-family: Roboto, sans-serif;
				line-height: 18px;
				font-weight: 400;
				font-size: 1rem;
				color: black;
			}
		}
	}
</style>
